'use client'

import type { CSSProperties, ReactNode } from 'react'
import { motion, useReducedMotion } from 'framer-motion'

interface RevealProps {
  children: ReactNode
  className?: string
  /** Décalage en secondes, pour faire apparaître une grille en cascade. */
  delay?: number
  as?: 'div' | 'li' | 'article' | 'section'
  /** Cible d'ancre, quand le bloc révélé est aussi une destination de lien. */
  id?: string
  /** Variables CSS locales, par exemple la couleur d'accent d'un produit. */
  style?: CSSProperties
}

/**
 * Apparition progressive au scroll.
 *
 * Primitive unique du projet : toutes les animations d'entrée passent par ici,
 * ce qui garantit un comportement homogène et le respect systématique de
 * prefers-reduced-motion (CLAUDE.md § 8).
 */
export function Reveal({ children, className, delay = 0, as = 'div', id, style }: RevealProps) {
  const prefersReducedMotion = useReducedMotion()
  const Component = motion[as]

  if (prefersReducedMotion) {
    const Static = as
    return (
      <Static className={className} id={id} style={style}>
        {children}
      </Static>
    )
  }

  return (
    <Component
      className={className}
      id={id}
      style={style}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </Component>
  )
}
